import { Link } from 'react-router-dom';
import { BRAND, DEVELOPMENT_COPY } from '@saesak/shared';
import { SiteFooter } from './SiteFooter';

/**
 * PC 소개 페이지 (§14.1) — 데스크톱 브라우저로 saesak.kr에 온 비로그인 방문자의 랜딩.
 * 이미지는 스토어 자산 파이프라인이 복사해 주는 /landing/*.png(§14.1 store-shots.mjs ④)를
 * 그대로 쓴다 — 화면이 바뀌어 스크린샷을 재생성하면 이 페이지도 함께 새 화면이 된다.
 * 폰·네이티브 앱에서는 이 페이지를 거치지 않고 곧장 로그인으로 간다(App.tsx AnonymousHome).
 */

type Feature = { img: string; alt: string; title: string; desc: string };

/** 섹션별 기능 카드 — 이미지 이름은 store-shots.mjs의 LANDING_SHOTS와 맞춘다 */
const PARENT_FEATURES: Feature[] = [
  {
    img: '02-daily-note',
    alt: '알림장 화면',
    title: '알림장',
    desc: '컨디션 태그와 사진으로 담는 하루. 다문화 가정을 위한 번역 보기도 있어요.',
  },
  {
    img: '05-growth',
    alt: '신체 성장 화면',
    title: '신체 성장',
    desc: '질병관리청 성장도표 위에서 키와 몸무게의 흐름을 봅니다.',
  },
  {
    img: '11-bus',
    alt: '등하원 버스 화면',
    title: '등하원 버스',
    desc: '지금 몇 정거장 전인지 지도로 보고, 도착이 가까워지면 알림을 받아요.',
  },
  {
    img: '08-meals',
    alt: '식단표 화면',
    title: '식단표',
    desc: '오늘의 급식 사진과 알레르기 유발 식품 표시를 한눈에.',
  },
];

const TEACHER_FEATURES: Feature[] = [
  {
    img: '15-note-editor',
    alt: '알림장 쓰기 화면',
    title: '알림장 쓰기',
    desc: '컨디션 12종 태그로 3분이면 끝나요. 표현이 고민되면 AI가 다듬어 줍니다.',
  },
  {
    img: '17-observations',
    alt: '관찰메모 화면',
    title: '관찰메모',
    desc: '하단 탭에서 바로 열고, 음성으로 받아써요. 발달기록의 근거로 이어집니다.',
  },
  {
    img: '28-care-journal',
    alt: '보육일지 화면',
    title: '계획안·보육일지',
    desc: '현장 서식 그대로 다섯 가지 양식. 일지는 반 관찰메모를 근거로 AI 초안을 받아요.',
  },
  {
    img: '16-development',
    alt: '발달기록 화면',
    title: '발달기록',
    desc: '관찰 근거를 붙여 7단계 새싹 눈금으로 기록하면, 학부모 돋보기로 전해집니다.',
  },
];

const DIRECTOR_FEATURES: Feature[] = [
  {
    img: '25-operations',
    alt: '앱 관리 화면',
    title: '앱 관리',
    desc: '반·원아·교직원·초대코드·구독을 한곳에서. 종이 명부가 필요 없어요.',
  },
  {
    img: '21-documents',
    alt: '전자 서류함 화면',
    title: '전자 서류함',
    desc: '투약의뢰서·동의서가 원아별로 자동 편철되고, 지도점검 묶음도 클릭 한 번.',
  },
  {
    img: '23-life-records',
    alt: '생활기록부 화면',
    title: '생활기록부',
    desc: '보편 종이 서식 그대로 PDF 발급. 평소 기록이 격자와 종합의견을 채워 줍니다.',
  },
  {
    img: '20-ops',
    alt: '운영 도구 화면',
    title: '운영 도구',
    desc: '안전점검 체크리스트·식자재 검수일지·근태관리까지 원 운영 서류를 앱에서.',
  },
];

const VALUE_PROPS = [
  {
    title: '비교하지 않는 발달 기록',
    desc: '또래 평균과 견주는 대신, 아이 자신의 시간축 위에서 자람을 봅니다. 판정하는 말 대신 관찰한 모습과 다음 걸음으로 씁니다.',
  },
  {
    title: '기록이 서류가 되는 구조',
    desc: '관찰메모가 발달기록의 근거가 되고, 매일의 기록이 보육일지와 생활기록부 PDF까지 이어집니다. 같은 내용을 두 번 쓰지 않아요.',
  },
  {
    title: '온 가족이 함께',
    desc: '보호자 여러 명이 함께 보고, 알림톡·문자·이메일로도 소식이 갑니다. 보호자와 교직원 개인에게는 비용을 받지 않아요.',
  },
];

function PhoneShot({
  img,
  alt,
  eager = false,
  className = '',
}: {
  img: string;
  alt: string;
  eager?: boolean;
  className?: string;
}) {
  return (
    <div className={`overflow-hidden rounded-[30px] bg-ink p-2 shadow-raised ${className}`}>
      <img
        src={`/landing/${img}.png`}
        alt={alt}
        width={780}
        height={1688}
        loading={eager ? 'eager' : 'lazy'}
        className="block w-full rounded-[22px]"
      />
    </div>
  );
}

function FeatureSection({
  id,
  label,
  title,
  desc,
  features,
}: {
  id: string;
  label: string;
  title: string;
  desc: string;
  features: Feature[];
}) {
  return (
    <section id={id} className="mx-auto w-full max-w-6xl scroll-mt-24 px-6 py-16">
      <p className="text-sm font-bold text-primary-strong">{label}</p>
      <h2 className="mt-1 text-2xl font-bold lg:text-[32px] lg:leading-tight">{title}</h2>
      <p className="mt-2 max-w-2xl text-base text-ink-sub">{desc}</p>
      <div className="mt-8 grid grid-cols-2 gap-6 lg:grid-cols-4">
        {features.map((feature) => (
          <div key={feature.img} className="flex flex-col gap-3">
            <PhoneShot img={feature.img} alt={feature.alt} />
            <div>
              <p className="font-bold">{feature.title}</p>
              <p className="mt-1 text-sm text-ink-sub">{feature.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function IntroPage() {
  return (
    <div className="min-h-screen bg-bg">
      {/* 상단 고정 바 */}
      <header className="sticky top-0 z-10 border-b border-line bg-bg/90 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-6 px-6">
          <a href="#top" className="flex items-center gap-2.5">
            <img src="/brand-icon.svg" alt="" width={30} height={30} />
            <span className="text-lg font-bold text-primary-strong">{BRAND.name}</span>
          </a>
          <nav className="hidden items-center gap-6 text-sm font-semibold text-ink-sub lg:flex">
            <a href="#parents" className="hover:text-ink">
              학부모
            </a>
            <a href="#teachers" className="hover:text-ink">
              선생님
            </a>
            <a href="#directors" className="hover:text-ink">
              원 운영
            </a>
            <Link to="/pricing" className="hover:text-ink">
              요금
            </Link>
          </nav>
          <Link
            to="/login"
            className="rounded-full bg-primary px-5 py-2 text-sm font-bold text-white hover:bg-primary-strong"
          >
            로그인
          </Link>
        </div>
      </header>

      {/* 히어로 */}
      <section id="top" className="mx-auto grid w-full max-w-6xl items-center gap-10 px-6 pb-10 pt-14 lg:grid-cols-[1.1fr_1fr] lg:pb-16">
        <div>
          <p className="text-base font-semibold text-primary-strong">{BRAND.tagline}</p>
          <h1 className="mt-3 text-[40px] font-bold leading-[1.25] tracking-tight lg:text-[52px]">
            아이의 하루와 성장을
            <br />
            따뜻하게 주고받는 곳
          </h1>
          <p className="mt-5 max-w-xl text-lg text-ink-sub">
            어린이집·유치원과 가정을 잇는 알림장, 비교하지 않는 발달기록, 그리고 생활기록부까지.
            매일의 소통이 그대로 아이의 기록이 됩니다.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/login"
              className="rounded-full bg-primary px-7 py-3 text-base font-bold text-white hover:bg-primary-strong"
            >
              로그인
            </Link>
            <Link
              to="/pricing"
              className="rounded-full border border-line-strong bg-surface px-7 py-3 text-base font-bold hover:border-primary"
            >
              요금 안내
            </Link>
          </div>
          <p className="mt-4 text-sm text-ink-sub">
            교직원·보호자는 기관이 보내 준 초대코드로 가입해요 · 휴대전화로 열면 바로 앱 화면입니다
          </p>
        </div>
        {/* 폰 목업 3대 — 가운데(홈)를 크게, 양옆은 살짝 내려 겹친다 */}
        <div className="mx-auto flex w-full max-w-lg items-end justify-center">
          <PhoneShot img="04-scope" alt="발달 돋보기 화면" className="z-0 -mr-10 mb-2 w-1/3 rotate-[-5deg]" />
          <PhoneShot img="01-parent-home" alt="학부모 홈 화면" eager className="z-10 w-[42%]" />
          <PhoneShot img="03-album" alt="앨범 화면" className="z-0 -ml-10 mb-2 w-1/3 rotate-[5deg]" />
        </div>
      </section>

      {/* 가치 3가지 */}
      <section className="border-y border-line bg-surface">
        <div className="mx-auto grid w-full max-w-6xl gap-8 px-6 py-12 lg:grid-cols-3">
          {VALUE_PROPS.map((value) => (
            <div key={value.title}>
              <h3 className="text-lg font-bold text-primary-strong">{value.title}</h3>
              <p className="mt-2 text-base text-ink-sub">{value.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <FeatureSection
        id="parents"
        label="학부모"
        title="오늘 하루가 홈 한 화면에"
        desc="등원 소식부터 알림장·앨범·식단·버스 위치까지, 물어보지 않아도 먼저 도착합니다. 투약의뢰서와 귀가동의서도 종이 대신 앱에서 서명해요."
        features={PARENT_FEATURES}
      />

      {/* 언어 원칙 밴드 — 발달 화면과 같은 그린 축(§7.4), 판정 어휘 없는 카피(§6.9) */}
      <section className="bg-primary-soft">
        <div className="mx-auto w-full max-w-6xl px-6 py-14 text-center">
          <p className="text-2xl font-bold text-primary-strong lg:text-[30px]">
            “{DEVELOPMENT_COPY.everyChildOwnPace}”
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-base text-ink-sub">
            {BRAND.name}의 발달 화면에는 또래 순위도, 점수도 없습니다. 관찰한 모습과 지금 익히는
            중인 것, 다음에 함께 해볼 것을 적습니다. 교사가 쓰는 문장도 같은 원칙을 따르도록 앱이
            표현을 함께 살펴 줍니다.
          </p>
        </div>
      </section>

      <FeatureSection
        id="teachers"
        label="선생님"
        title="기록은 가볍게, 근거는 단단하게"
        desc="알림장 3분, 관찰은 말로. 흩어져 있던 계획안·일지·발달기록이 한 흐름으로 이어져 같은 내용을 두 번 쓰지 않습니다."
        features={TEACHER_FEATURES}
      />

      <FeatureSection
        id="directors"
        label="원장님"
        title="원 운영 서류가 저절로 정리되는 곳"
        desc="서류함이 자동으로 편철되고, 생활기록부는 보편 서식 PDF로 발급됩니다. 지도점검 준비가 클릭 몇 번으로 끝나요."
        features={DIRECTOR_FEATURES}
      />

      {/* 도입 안내 밴드 */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-4 px-6 py-14 text-center">
          <h2 className="text-2xl font-bold lg:text-[30px]">우리 원에서 시작하려면</h2>
          <p className="max-w-2xl text-base text-ink-sub">
            {BRAND.name}는 기관 단위 연간 구독이에요(VAT 별도). 보호자와 교직원 개인에게는 비용을
            받지 않고, 신규 기관에는 무료 체험 기간이 제공됩니다.
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            <Link
              to="/pricing"
              className="rounded-full bg-primary px-7 py-3 text-base font-bold text-white hover:bg-primary-strong"
            >
              요금 안내 보기
            </Link>
            <Link
              to="/login"
              className="rounded-full border border-line-strong bg-bg px-7 py-3 text-base font-bold hover:border-primary"
            >
              로그인
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-6xl px-6">
        <SiteFooter />
      </div>
    </div>
  );
}
